/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState, useEffect, useMemo } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from './useAbortController';
import { isObjEmptyOrNull, getErrorFromEndpoint, isStrEmptyOrNull } from '../UtilityFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { AxiosError } from 'axios';
import { useLoanContext } from '../../contexts/LoanContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export function useEventDateList(eventCode: string = '') {
  const { token } = useAuthContext();
  const { loanNumber } = useLoanContext();
  const { get } = useAbortController();
  const [errors, setErrors] = useState('');
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['eventlist', eventCode, loanNumber, token], [eventCode, loanNumber, token]);

  const handleError = useCallback((res: Error | AxiosError | any) => {
    const err = getErrorFromEndpoint(res);
    if (isStrEmptyOrNull(err)) return '';
    console.log('Error in eventDatesListAPI: ' + err);
    setErrors(err);
    return err;
  }, []);

  const eventDatesListAPI = useCallback(
    async (eventCodes: string = ''): Promise<Array<EventObj>> => {
      if (!loanNumber || !token) return [];
      const ec = eventCode || eventCodes;
      const request = {
        url: isStrEmptyOrNull(ec)
          ? process.env.REACT_APP_EVENTS_LIST.replace('?', loanNumber)
          : process.env.REACT_APP_EVENTS_LIST.replace('?', loanNumber) + '?eventCodes=' + ec,
        method: 'get',
      };
      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };
      const config = get();
      const response = await axiosCall(request, config, headers)
        .then((res) => {
          if (isObjEmptyOrNull(res)) return [];
          const err = handleError(res);
          return isStrEmptyOrNull(err) ? res : [];
        })
        .catch((e: AxiosError | Error | any) => {
          handleError(e);
          return [];
        });
      return response;
    },
    [get, token, handleError, loanNumber, eventCode],
  );

  const getEventDateList = useCallback(
    async (eventCodes: string): Promise<Array<EventObj>> => {
      setErrors('');
      setLoading(true);
      const res = await eventDatesListAPI(eventCodes)
        .then((res) => {
          return res;
        })
        .finally(() => {
          setLoading(false);
        });
      return res;
    },
    [eventDatesListAPI],
  );

  // the following function gets the list of event occurrences for the event code that aren't 00 (which just gets the latest occurrence)
  // for ex, if the event date list returns 00, 01, 02, the result will be [01, 02] sorted
  // 00 points to another occurrence, which is why we discard it
  const getEventOccrArr = useCallback((res: Array<EventObj>) => {
    let eventDatesSet = new Set();
    res.forEach((element) => {
      if (element.occurance !== '00') {
        eventDatesSet.add(element.occurance);
      }
    });
    let eventDatesArr = Array.from(eventDatesSet);
    if (isObjEmptyOrNull(eventDatesArr)) return null;
    eventDatesArr.sort();
    return eventDatesArr;
  }, []);

  // gets the list of events for a specific event code
  // out of the events, gets the event obj that matches the occurrence requested
  const getEventRequestedObj = useCallback(
    (res: Array<EventObj>, occur: number): EventObj | null => {
      const eventDatesArr = getEventOccrArr(res);
      if (isObjEmptyOrNull(eventDatesArr)) return null;

      // if occurrence is greater than the number of occurrences available (minus the 00 occurrence which points to another occurrence)
      // for instance if there are 2 occurrences available and the occurrence requested is 3, return
      if (occur > eventDatesArr.length - 1) return null;

      // get the occurrence requested from the array of events
      // for ex, eventDatesArr = [01,02], occur = 1 (past) (length = 2, occur = 1)
      // 2 - 1 - 1 = 0, which is the index of 01, so occrReq = 01 (eventDatesArr[0])
      let occrReq = eventDatesArr[eventDatesArr.length - occur - 1];

      // gets the event matching the occurrence
      let occrReqObj = res.find((elem) => elem.occurance === occrReq);
      if (isObjEmptyOrNull(occrReqObj)) return null;

      return occrReqObj;
    },
    [getEventOccrArr],
  );

  const getEventCodeSpecificOccurrence = useCallback(
    async (occur: number, eventCode: string): Promise<EventObj | null> => {
      // get all events with event code specified
      const eventObj = await getEventDateList(eventCode).then((res) => {
        if (isObjEmptyOrNull(res)) return null;
        const err = handleError(res);
        if (!isStrEmptyOrNull(err)) return null;

        // get AEA event requested out of the AEA events available
        return getEventRequestedObj(res, occur);
      });
      return eventObj;
    },
    [getEventDateList, getEventRequestedObj],
  );

  // Fetch communication data using react-query
  const { data, isFetching, isLoading } = useQuery({
    queryKey: queryKey,
    queryFn: ({ signal }: { signal: AbortSignal }) => {
      return getEventDateList(eventCode);
    },
    refetchOnWindowFocus: false,
  });

  const refetchEvents = useCallback(() => queryClient.invalidateQueries({ queryKey }), [queryClient, queryKey]);

  return {
    events: data && !isFetching && !isLoading ? data : [],
    statuses: {
      loading: loading || isFetching || isLoading,
      errors: errors,
    },
    getEventDateList,
    getEventCodeSpecificOccurrence,
    refetchEvents,
  };
}

export default useEventDateList;
